import React, { useEffect, useState, useContext } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { useLocation } from "react-router-dom"
import { API, Auth } from "aws-amplify"
import { useSnackbar } from "notistack"

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  CircularProgress,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import GeneralTab from "./GeneralTab"
import NumerationTab from "./NumerationTab"
import DecimalsTab from "./DecimalsTab"
import ModesTab from "./ModesTab"
import CertificationTab from "./CertificationTab"

import ModalCargandoRecalc from "components/Budgeting2023/options/ModalCargandoRecalc"

import ProjectContext from "../../../../context/projects/projectContext"
import Budget2023Context from "../../../../context/budget2023/Budget2023Context"

export default function ModalEditProject({
  open,
  onClose,
  onSave,
  projectInfo,
}) {
  const [tab, setTab] = useState(0)
  const [localProject, setLocalProject] = useState({ ...projectInfo })

  useEffect(() => {}, [localProject])
  const [openAdd, setOpenAdd] = useState(false)
  const [loading, setLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { updateProject, getProject, updateCoefficients, updateBaseCertMode } =
    useContext(ProjectContext)
  const { setBudgetMode, setBaseCert } = useContext(Budget2023Context)
  const [openRecalcular, setOpenRecalcular] = useState(false)
  const [decimalsTouched, setDecimalsTouched] = useState(false)
  const [baseCertTouched, setBaseCertTouched] = useState(false)
  const [canChangeBaseCert, setCanChangeBaseCert] = useState(false)

  const location = useLocation()

  useEffect(() => {
    if (!open) {
      return
    }

    setLocalProject({ ...projectInfo })
    setDecimalsTouched(false)
    setBaseCertTouched(false)
    canChangeBaseCertRequest(projectInfo?.projectId)
  }, [open, projectInfo?.projectId])

  const decimalsChanged = () => !!decimalsTouched

  const canChangeBaseCertRequest = async projectId => {
    if (!projectId) {
      setCanChangeBaseCert(false)
      return
    }

    try {
      const res = await API.get(
        "CORE",
        `/cert/can_change_cert_base/${projectId}/`,
        {},
      )
      setCanChangeBaseCert(res)
    } catch (error) {
      console.error("Error fetching canChangeBaseCert:", error)
      setCanChangeBaseCert(false)
    }
  }

  const handleSave = async () => {
    setLoading(true)
    let shouldRecalculate = false
    try {
      let data = { ...localProject }
      if (data.projectImage !== undefined) {
        data.imagen = data.projectImage
      }
      // IVA / IGIC: números finitos; IGIC se guarda ×100 (entero)
      data.iva =
        data.iva != null && Number.isFinite(Number(data.iva))
          ? parseInt(Number(data.iva), 10)
          : 0
      data.igic =
        data.igic != null && Number.isFinite(Number(data.igic))
          ? Math.round(Number(data.igic))
          : 0

      if (decimalsChanged()) {
        shouldRecalculate = true
        await updateCoefficients(localProject)
      }

      if (baseCertTouched) {
        try {
          await updateBaseCertMode(localProject)
          setBaseCert(localProject.base_cert)
        } catch (err) {
          enqueueSnackbar(i18n.t("editProject.errorUpdatingBaseCertMode"), {
            variant: "error",
          })
        }
      }

      await updateProject(data)
      setBudgetMode(data.budgetMode)
      await getProject(data.projectId)
      if (onSave) onSave()
    } finally {
      if (shouldRecalculate) {
        setOpenRecalcular(true)
      } else {
        setLoading(false)
        onClose()
      }
    }
  }

  const handleRecalcFinished = () => {
    if (location.pathname === "/dashboard") {
      setLoading(false)
      onClose()
    } else {
      window.location.reload()
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>
          {i18n.t("editProject.editProject")}
          <IconButton
            aria-label={i18n.t("editProject.close")}
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 400,
            maxWidth: 600,
            maxHeight: "70vh",
            minHeight: 396,
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 1,
            }}
          >
            <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
              <Tab label={i18n.t("editProject.general")} />
              <Tab label={i18n.t("editProject.numeration")} />
              <Tab label={i18n.t("editProject.decimals")} />
              <Tab label={i18n.t("editProject.modes")} />
              <Tab label={i18n.t("editProject.certification")} />
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto", paddingTop: 1 }}>
            {tab === 0 && (
              <GeneralTab
                localProject={localProject}
                setLocalProject={setLocalProject}
                openAdd={openAdd}
                setOpenAdd={setOpenAdd}
              />
            )}
            {tab === 1 && (
              <NumerationTab
                localProject={localProject}
                setLocalProject={setLocalProject}
              />
            )}
            {tab === 2 && (
              <DecimalsTab
                localProject={localProject}
                setLocalProject={setLocalProject}
                setDecimalsTouched={setDecimalsTouched}
              />
            )}
            {tab === 3 && (
              <ModesTab
                localProject={localProject}
                setLocalProject={setLocalProject}
              />
            )}
            {tab === 4 && (
              <CertificationTab
                localProject={localProject}
                setLocalProject={setLocalProject}
                setBaseCertTouched={setBaseCertTouched}
                canChangeBaseCert={canChangeBaseCert}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="branded"
            disabled={loading}
            startIcon={null}
            onClick={onClose}
          >
            {i18n.t("editProject.close")}
          </Button>
          <Button
            variant="contained"
            color="branded"
            onClick={handleSave}
            disabled={loading}
            startIcon={null}
            sx={{
              width: 110,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              i18n.t("editProject.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {openRecalcular && (
        <ModalCargandoRecalc
          open={openRecalcular}
          setOpen={setOpenRecalcular}
          projectIdProp={localProject.projectId}
          onCloseFinished={handleRecalcFinished}
        />
      )}
    </>
  )
}

ModalEditProject.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  projectInfo: PropTypes.object.isRequired,
}
